import * as React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { StatusChip, type StatusChipTone } from '@/components/ui/status-chip';
import {
  useDocumentAiRunsQuery,
  useReadAgainMutation,
  type AiRun,
  type AiRunProposal,
  type DocumentAiWorker,
} from '@/lib/api/ai-runs';
import { formatRelative, titleCase } from '@/lib/utils/format';

/**
 * "What the AI did" on a document (T-0239; E21-F04, E21-design §9).
 *
 * One card, one row per AI worker that looked at the document, from the run
 * ledger: the worker's plain label, the outcome in words ("Read · proposed
 * 3", "Skipped · Health Check is off", "Waiting for the baseline", "Failed ·
 * AI budget used up"), when, the proposals it made from this document (each
 * a link into the review queue), and the earlier reads behind a disclosure.
 * "Read again" shows where the server says it applies (a skipped or failed
 * read, engagement lead and above); a waiting row is re-read on its own when
 * the baseline or budget arrives. The Discovery workers (E23, T-0254) are
 * rows like any other: their labels come from the registry through the API,
 * and a Read again on a placed transcript starts a new reading through its
 * interview.
 *
 * Tokens only (StatusChip tones, ink scale). The proposal link is a render
 * prop so the card stays router-free (and testable), as the delete control's
 * requirements link is.
 */

/** How long the card waits for a requested read to show up before offering it again. */
const ASK_WAIT_MS = 2 * 60_000;

const MODULE_LABEL: Record<string, string> = {
  contract_compliance: 'Contract Compliance',
  health_check: 'Health Check',
  go_live_readiness: 'Go-Live Readiness',
  discovery: 'Discovery',
};

/** Reasons a read is held until something else exists; it is re-read on its own. */
const WAITING: Record<string, string> = {
  no_baseline: 'Waiting for the baseline',
  no_budget_baseline: 'Waiting for a budget',
};

const SKIPPED: Record<string, string> = {
  party_not_read: "not read for this document's party",
  type_not_read: 'not a document type it reads',
  coalesced: 'a check was already queued',
  document_missing: 'the document was removed',
  no_chunks: 'no readable text',
  in_flight: 'another read was in progress',
  run_in_flight: 'another read was in progress',
  run_lost: 'taken over by another run',
  superseded: 'taken over by another run',
  first_run: 'the first baseline has not run yet',
  // Discovery (E23-design §3).
  discovery_not_started: 'Discovery is not set up on this engagement',
  not_placed: 'place it in an interview first',
  already_placed: 'already attached to an interview',
  health_interview_note: 'a Health Check interview note, not a Discovery transcript',
  unreadable: 'the file could not be read as a transcript',
};

/** The interview finder (T-0261): its one proposal is a placement, confirmed on Sessions. */
const INTERVIEW_FINDER = 'discovery_interview';

const FAILED: Record<string, string> = {
  budget_exhausted: 'AI budget used up',
  rate_limited: 'AI service busy',
  model_error: 'AI service error',
  invalid_output: "the AI's answer could not be used",
  timeout: 'took too long',
  internal_error: 'internal error',
};

const PROPOSAL_STATUS: Record<string, string> = {
  pending: 'to review',
  accepted: 'accepted',
  rejected: 'rejected',
  overridden: 'rated differently',
  superseded: 'replaced',
  revised: 'sent back',
};

function words(code: string): string {
  return code.replace(/_/g, ' ');
}

/** The outcome of one read, in words. */
export function outcomeText(run: AiRun, module: string | null, worker?: string): string {
  const reason = run.reason ?? '';
  switch (run.status) {
    case 'running':
      return 'Reading…';
    case 'succeeded': {
      const read = reason === 'degraded' ? 'Read in part' : 'Read';
      if (worker === INTERVIEW_FINDER) {
        if (run.proposals_created > 0) return 'Proposed a placement';
        return reason === 'already_proposed'
          ? 'Placement already proposed'
          : `${read} · nothing new`;
      }
      if (run.proposals_created > 0) return `${read} · proposed ${run.proposals_created}`;
      return reason === 'nothing_found' ? `${read} · nothing found` : `${read} · nothing new`;
    }
    case 'skipped': {
      if (WAITING[reason]) return WAITING[reason]!;
      if (reason === 'already_read') return 'Already read';
      if (reason === 'nothing_found') return 'Read · nothing found';
      if (reason === 'module_off') {
        return `Skipped · ${(module && MODULE_LABEL[module]) || 'the module'} is off`;
      }
      return `Skipped · ${SKIPPED[reason] ?? (reason ? words(reason) : 'no reason given')}`;
    }
    case 'failed':
      return `Failed · ${FAILED[reason] ?? (reason ? words(reason) : 'no reason given')}`;
    default:
      return titleCase(run.status);
  }
}

function outcomeTone(run: AiRun): StatusChipTone {
  switch (run.status) {
    case 'running':
      return 'ai';
    case 'succeeded':
      return run.proposals_created > 0 ? 'healthy' : 'neutral';
    case 'failed':
      return 'critical';
    case 'skipped':
      return WAITING[run.reason ?? ''] ? 'amber' : 'unassessed';
    default:
      return 'neutral';
  }
}

const TRIGGER: Record<string, string> = {
  manual: 'read again by a person',
  revise: 'after a revise',
  catch_up: 'caught up',
  recheck: 're-check',
};

function when(run: AiRun, now: number): string {
  const at = formatRelative(run.finished_at ?? run.started_at, now);
  const trigger = TRIGGER[run.trigger];
  return trigger ? `${at} · ${trigger}` : at;
}

export interface AiRunsCardProps {
  engagementId: string;
  documentId: string;
  /**
   * A link to one proposal: the review queue for the IV&V workers; Findings
   * by area for a Discovery proposal (T-0257); for the interview finder
   * (`proposal_type` `discovery_interview`) the Sessions page, where a
   * placement is confirmed (T-0261).
   */
  renderProposalLink: (
    proposalId: string,
    label: string,
    proposal: AiRunProposal,
  ) => React.ReactNode;
  /** For tests: the clock the relative times are read against. */
  now?: number;
}

export function AiRunsCard({ engagementId, documentId, renderProposalLink, now }: AiRunsCardProps) {
  // Workers a person asked to read again in this visit, with the run that was
  // latest when they asked: the card polls until a new row replaces it (run
  // ids, not clocks, so a skew between browser and server cannot clear it).
  const [asked, setAsked] = React.useState<ReadonlyMap<string, string>>(new Map());
  const query = useDocumentAiRunsQuery(engagementId, documentId, { poll: asked.size > 0 });
  const readAgain = useReadAgainMutation(engagementId, documentId);

  React.useEffect(() => {
    if (asked.size === 0 || !query.data) return;
    const next = new Map(asked);
    for (const w of query.data.workers) {
      if (next.has(w.worker) && next.get(w.worker) !== w.latest.id) next.delete(w.worker);
    }
    if (next.size !== asked.size) setAsked(next);
  }, [query.data, asked]);

  // A read that never lands (the worker skipped it without a row) stops
  // being waited for after a while; the button comes back.
  const timers = React.useRef<number[]>([]);
  React.useEffect(() => () => timers.current.forEach((t) => window.clearTimeout(t)), []);

  function onReadAgain(w: DocumentAiWorker) {
    readAgain.mutate(w.worker, {
      onSuccess: () => {
        setAsked((prev) => new Map(prev).set(w.worker, w.latest.id));
        timers.current.push(
          window.setTimeout(
            () =>
              setAsked((prev) => {
                const next = new Map(prev);
                next.delete(w.worker);
                return next;
              }),
            ASK_WAIT_MS,
          ),
        );
      },
    });
  }

  const clock = now ?? Date.now();

  return (
    <Card>
      <CardHeader>
        <CardTitle>What the AI did</CardTitle>
        <CardDescription>
          Every AI reader that looked at this document: what it proposed, or why it skipped or
          failed.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {query.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-ink-500">
            <Spinner /> Loading…
          </div>
        ) : query.error ? (
          <ErrorMessage error={query.error} />
        ) : !query.data || query.data.workers.length === 0 ? (
          <p className="text-sm text-ink-500">No AI reads are recorded for this document yet.</p>
        ) : (
          <>
            {readAgain.error ? <ErrorMessage error={readAgain.error} /> : null}
            <ul className="divide-y divide-ink-100">
              {query.data.workers.map((w) => (
                <WorkerRow
                  key={w.worker}
                  worker={w}
                  now={clock}
                  asked={asked.has(w.worker)}
                  pending={readAgain.isPending && readAgain.variables === w.worker}
                  onReadAgain={() => onReadAgain(w)}
                  renderProposalLink={renderProposalLink}
                />
              ))}
            </ul>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function WorkerRow({
  worker,
  now,
  asked,
  pending,
  onReadAgain,
  renderProposalLink,
}: {
  worker: DocumentAiWorker;
  now: number;
  asked: boolean;
  pending: boolean;
  onReadAgain: () => void;
  renderProposalLink: AiRunsCardProps['renderProposalLink'];
}) {
  const { latest } = worker;
  const more = worker.proposals_total - worker.proposals.length;
  return (
    <li className="flex flex-wrap items-start gap-x-4 gap-y-2 py-3" data-worker={worker.worker}>
      <div className="w-52 flex-none text-sm font-medium text-ink-900">{worker.label}</div>
      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <StatusChip tone={outcomeTone(latest)}>
            {outcomeText(latest, worker.module, worker.worker)}
          </StatusChip>
          <span className="text-meta text-ink-500">
            {asked ? 'Read again requested' : when(latest, now)}
          </span>
        </div>
        {worker.proposals.length > 0 ? (
          <ul className="space-y-1 text-sm">
            {worker.proposals.map((p) => (
              <ProposalLine key={p.id} proposal={p} renderProposalLink={renderProposalLink} />
            ))}
            {more > 0 ? (
              <li className="text-meta text-ink-500">and {more} more in the review queue</li>
            ) : null}
          </ul>
        ) : null}
        {worker.history.length > 0 ? (
          <details className="text-meta text-ink-500">
            <summary className="cursor-pointer select-none">
              Earlier reads ({worker.history.length})
            </summary>
            <ul className="mt-1 space-y-0.5 pl-4">
              {worker.history.map((r) => (
                <li key={r.id}>
                  {outcomeText(r, worker.module, worker.worker)} · {when(r, now)}
                </li>
              ))}
            </ul>
          </details>
        ) : null}
      </div>
      {worker.can_read_again && !asked ? (
        <Button type="button" variant="outline" size="sm" disabled={pending} onClick={onReadAgain}>
          {pending ? 'Asking…' : 'Read again'}
        </Button>
      ) : null}
    </li>
  );
}

function ProposalLine({
  proposal,
  renderProposalLink,
}: {
  proposal: AiRunProposal;
  renderProposalLink: AiRunsCardProps['renderProposalLink'];
}) {
  const label =
    proposal.proposal_type === 'discovery_interview'
      ? 'Interview placement: place it on Sessions'
      : (proposal.headline ?? titleCase(proposal.proposal_type));
  return (
    <li className="flex min-w-0 items-baseline gap-2">
      <span className="min-w-0 truncate">{renderProposalLink(proposal.id, label, proposal)}</span>
      <span className="flex-none text-meta text-ink-500">
        {PROPOSAL_STATUS[proposal.status] ?? words(proposal.status)}
      </span>
    </li>
  );
}
